import { useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useAuthContext } from '../auth'
import { ProfileService } from '../../services/profile/profileService'
import { useCachedLoader } from '../../hooks/useCachedLoader'
import { ProfileContext, type ProfileContextType } from './ProfileContextDef'
import type { Profile, ProfileEndpoints } from '@bandos/shared'

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext()

  const {
    data: profile,
    loading,
    setData: setProfile,
    reload: reloadProfile,
  } = useCachedLoader<Profile | null>(user?.id ?? null, async () => ProfileService.get(), {
    initialValue: null,
  })

  const updateProfile = useCallback(
    async (updates: ProfileEndpoints['update']['body']) => {
      const result = await ProfileService.update(updates)
      if (result.data) {
        setProfile(result.data)
      }
      return result
    },
    [setProfile]
  )

  const value = useMemo<ProfileContextType>(
    () => ({ profile, loading, updateProfile, reloadProfile }),
    [profile, loading, updateProfile, reloadProfile]
  )

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
}
